import React, { useEffect, useRef, useState } from 'react';
import {
  commitImport, getRun, ImportMetadata, ImportRun, Mapping, previewImport, PreviewResult,
} from '../api/lenderResponseImport';
import { Count, styles } from './LenderResponseImport';

/**
 * Import several tabs of one workbook together (Dec, 27 Oct 2026: the MBFS
 * sheets carry "New Complaints Logged" and "Complaints Not Logged" and both
 * get imported every week).
 *
 * Each tab is previewed on its own (its own headers, mapping, counts, clashes
 * and duplicate check), then the imports run one after another as separate
 * runs, so the history, reports and undo stay per tab. The batch label gets
 * the tab name appended so the two runs are told apart.
 */
export interface LenderResponseMultiTabProps {
  file: File;
  meta: ImportMetadata;
  password: string;
  sheets: string[];
  onBack: () => void;
  onFinished: () => void;
}

type PlanStatus = 'previewing' | 'ready' | 'blocked' | 'queued' | 'running' | 'done' | 'failed';

interface TabPlan {
  sheet: string;
  status: PlanStatus;
  preview: PreviewResult | null;
  mapping: Mapping;
  allowDuplicate: boolean;
  overrideClashes: boolean;
  error: string | null;
  run: ImportRun | null;
  showMapping: boolean;
}

const POLL_MS = 2000;

export function tabBatchLabel(batchLabel: string, sheet: string): string {
  const slug = sheet.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return slug ? `${batchLabel}-${slug}` : batchLabel;
}

function readiness(plan: TabPlan): PlanStatus {
  if (!plan.preview) return 'previewing';
  if (plan.preview.needsPassword || (plan.preview.headerErrors?.length ?? 0) > 0) return 'blocked';
  if (!plan.preview.mappingOk) return 'blocked';
  if (plan.preview.duplicateOfRun && !plan.allowDuplicate) return 'blocked';
  return 'ready';
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const LenderResponseMultiTab: React.FC<LenderResponseMultiTabProps> = ({
  file, meta, password, sheets, onBack, onFinished,
}) => {
  const [plans, setPlans] = useState<TabPlan[]>(() => sheets.map((sheet) => ({
    sheet, status: 'previewing', preview: null, mapping: {}, allowDuplicate: false,
    overrideClashes: false, error: null, run: null, showMapping: false,
  })));
  const [importing, setImporting] = useState(false);
  const [finished, setFinished] = useState(false);
  const cancelled = useRef(false);

  const update = (sheet: string, patch: Partial<TabPlan> | ((p: TabPlan) => Partial<TabPlan>)) =>
    setPlans((prev) => prev.map((p) => {
      if (p.sheet !== sheet) return p;
      const next = { ...p, ...(typeof patch === 'function' ? patch(p) : patch) };
      if (!['queued', 'running', 'done', 'failed'].includes(next.status)) next.status = readiness(next);
      return next;
    }));

  const previewTab = async (sheet: string, mapping?: Mapping) => {
    update(sheet, { status: 'previewing', error: null });
    try {
      const result = await previewImport(file, { ...meta, batchLabel: tabBatchLabel(meta.batchLabel, sheet) }, {
        sheet, mapping, password,
      });
      update(sheet, (p) => ({
        preview: result,
        mapping: result.effectiveMapping || p.mapping,
        status: 'ready',
      }));
    } catch (e: any) {
      update(sheet, { error: e?.response?.data?.error || 'Preview failed', status: 'blocked' });
    }
  };

  // Preview every tab, one after the other, when the plan is created.
  useEffect(() => {
    cancelled.current = false;
    (async () => {
      for (const sheet of sheets) {
        if (cancelled.current) return;
        // eslint-disable-next-line no-await-in-loop
        await previewTab(sheet);
      }
    })();
    return () => { cancelled.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheets.join('|')]);

  const remap = (sheet: string, key: string, header: string) => {
    const plan = plans.find((p) => p.sheet === sheet);
    if (!plan) return;
    const next = { ...plan.mapping, [key]: header || null };
    update(sheet, { mapping: next });
    previewTab(sheet, next);
  };

  const allReady = plans.length > 0 && plans.every((p) => p.status === 'ready');

  const importAll = async () => {
    if (!allReady) return;
    setImporting(true);
    setPlans((prev) => prev.map((p) => ({ ...p, status: 'queued' })));
    for (const plan of plans) {
      if (cancelled.current) break;
      update(plan.sheet, { status: 'running' });
      try {
        const { runId } = await commitImport(file, { ...meta, batchLabel: tabBatchLabel(meta.batchLabel, plan.sheet) }, {
          sheet: plan.sheet,
          mapping: plan.mapping,
          expectedSha256: plan.preview!.fileSha256!,
          expectedPreviewToken: plan.preview!.previewToken!,
          allowDuplicateFile: plan.allowDuplicate,
          overrideOutcomeClashes: plan.overrideClashes,
          password,
        });
        let doc: ImportRun = await getRun(runId);
        update(plan.sheet, { run: doc });
        while (doc.status === 'running' && !cancelled.current) {
          // eslint-disable-next-line no-await-in-loop
          await sleep(POLL_MS);
          // eslint-disable-next-line no-await-in-loop
          doc = await getRun(runId);
          update(plan.sheet, { run: doc });
        }
        update(plan.sheet, { status: doc.status === 'completed' ? 'done' : 'failed', run: doc });
        if (doc.status !== 'completed') break;
      } catch (e: any) {
        update(plan.sheet, { status: 'failed', error: e?.response?.data?.error || 'Import failed to start' });
        break;
      }
    }
    setImporting(false);
    setFinished(true);
    onFinished();
  };

  const statusLabel: Record<PlanStatus, string> = {
    previewing: 'Checking…', ready: 'Ready', blocked: 'Needs attention', queued: 'Queued',
    running: 'Importing…', done: 'Done', failed: 'Failed',
  };

  return (
    <section style={styles.card} data-testid="multi-tab-import">
      <h3 style={styles.h3}>2 — Import {sheets.length} tabs together</h3>
      <p style={styles.note}>
        Each tab is checked on its own below. They import one after another as separate runs, each with the
        batch label and the tab name, so the history and reports stay per tab.
      </p>

      {plans.map((plan) => {
        const counts = plan.preview?.counts;
        const required = (plan.preview?.vocabulary || []).filter((f) => f.required || plan.mapping[f.key]);
        return (
          <div key={plan.sheet} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12 }} data-testid={`tab-plan-${plan.sheet}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <strong>{plan.sheet}</strong>
              <span style={plan.status === 'blocked' || plan.status === 'failed' ? styles.pillMissing : styles.pillOk}>
                {statusLabel[plan.status]}
              </span>
            </div>
            <div style={styles.note}>Batch label: <code>{tabBatchLabel(meta.batchLabel, plan.sheet)}</code></div>

            {plan.error && <div style={styles.errorBanner}>{plan.error}</div>}
            {plan.preview?.needsPassword && <div style={styles.errorBanner}>This tab asked for a password again; go back and check the password.</div>}
            {(plan.preview?.headerErrors?.length ?? 0) > 0 && (
              <div style={styles.errorBanner}>{plan.preview!.headerErrors!.map((e) => <div key={e}>{e}</div>)}</div>
            )}

            {plan.preview?.fileHeaders && (
              <>
                {plan.preview.mappingOk ? (
                  <div style={styles.okBanner}>
                    All required fields are mapped.{' '}
                    <button type="button" style={styles.linkBtn} onClick={() => update(plan.sheet, { showMapping: !plan.showMapping })}>
                      {plan.showMapping ? 'hide mapping' : 'show mapping'}
                    </button>
                  </div>
                ) : (
                  <div style={styles.warnBanner}>
                    {(plan.preview.mappingErrors || []).map((e) => <div key={e}>{e}</div>)}
                    <div>This tab will not import until every required field is mapped.</div>
                  </div>
                )}
                {(plan.showMapping || !plan.preview.mappingOk) && (
                  <table style={styles.table}>
                    <tbody>
                      {required.map((f) => (
                        <tr key={f.key}>
                          <td style={styles.td}>{f.label}{f.required && <span style={styles.requiredStar}> *</span>}</td>
                          <td style={styles.td}>
                            <select
                              value={plan.mapping[f.key] || ''}
                              onChange={(e) => remap(plan.sheet, f.key, e.target.value)}
                              style={styles.select}
                              aria-label={`${plan.sheet}: ${f.label}`}
                            >
                              <option value="">— not in this tab —</option>
                              {(plan.preview!.fileHeaders || []).map((h) => <option key={h} value={h}>{h}</option>)}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}

            {counts && (
              <>
                <div style={styles.countsGrid}>
                  <Count label="Rows" value={counts.totalRows} />
                  <Count label="Claims" value={counts.uniqueClaims} />
                  <Count label="Matched (live)" value={counts.matchedLive} />
                  <Count label="Matched (cancelled)" value={counts.matchedCancelled} />
                  <Count label="Not found" value={counts.unmatched} warn />
                  <Count label="Outcome clashes" value={counts.outcomeClashes} warn />
                </div>
                <div style={styles.note}>
                  Outcomes: {Object.entries(counts.byOutcome).map(([k, v]) => `${k} (${v})`).join(' · ') || '—'}
                </div>
              </>
            )}

            {plan.preview?.duplicateOfRun && (
              <div style={styles.warnBanner}>
                <strong>This tab was already imported</strong> — run {plan.preview.duplicateOfRun.runId} by{' '}
                {plan.preview.duplicateOfRun.startedBy} on {plan.preview.duplicateOfRun.startedAt?.slice(0, 10)}.
                <label style={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={plan.allowDuplicate}
                    onChange={(e) => update(plan.sheet, { allowDuplicate: e.target.checked })}
                    disabled={importing || finished}
                  />
                  Import this tab again anyway
                </label>
              </div>
            )}
            {(plan.preview?.problems?.outcomeClashes.length ?? 0) > 0 && (
              <details style={styles.problem}>
                <summary>{plan.preview!.problems!.outcomeClashes.length} outcome clash(es) — skipped unless overridden</summary>
                <div style={styles.problemBody}>
                  {plan.preview!.problems!.outcomeClashes.map((c) => (
                    <div key={c.claimId}>{c.claimId}: has “{c.existing}”, file says “{c.incoming}”</div>
                  ))}
                  <label style={styles.checkboxRow}>
                    <input
                      type="checkbox"
                      checked={plan.overrideClashes}
                      onChange={(e) => update(plan.sheet, { overrideClashes: e.target.checked })}
                      disabled={importing || finished}
                    />
                    Overwrite clashing outcomes on this tab
                  </label>
                </div>
              </details>
            )}
            {(plan.preview?.problems?.unmatched.length ?? 0) > 0 && (
              <details style={styles.problem}>
                <summary>{plan.preview!.problems!.unmatched.length} reference(s) not found — reported, not imported</summary>
                <div style={styles.problemBody}>{plan.preview!.problems!.unmatched.join(', ')}</div>
              </details>
            )}

            {plan.run && (
              <div style={{ marginTop: 8 }}>
                <div style={styles.progressOuter}>
                  <div style={{ ...styles.progressInner, width: `${plan.run.percent ?? (plan.status === 'done' ? 100 : 0)}%` }} />
                </div>
                <div style={styles.note}>
                  Run {plan.run.id}: {plan.run.status}
                  {plan.run.counts && ` — written ${plan.run.counts.written} · not found ${plan.run.counts.notFound} · clashes skipped ${plan.run.counts.clashSkipped} · errors ${plan.run.counts.errors}`}
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {!finished && (
          <button
            type="button"
            onClick={importAll}
            disabled={!allReady || importing}
            style={styles.primaryBtn}
          >
            {importing ? 'Importing…' : `Apply — import ${plans.length} tabs`}
          </button>
        )}
        <button type="button" onClick={onBack} disabled={importing} style={styles.secondaryBtn}>
          {finished ? 'Start another import' : 'Back to one tab'}
        </button>
        {!allReady && !importing && !finished && (
          <span style={styles.note}>Every tab must show “Ready” before the import can start.</span>
        )}
      </div>
    </section>
  );
};

export default LenderResponseMultiTab;
